import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Form, Popconfirm, Row, Tooltip } from 'antd';
import i18n from 'i18next';

import {
  SHIPMENT_RATE_ENTRY_BUTTON_REMOVE_STATUSES,
  SHIPMENT_RATE_ENTRY_DELETE_BUTTON_VISIBLE_STATUSES,
  USER_PREFERENCES,
} from '../../../../../../contants/common';
import useIs12HoursTimeFormat from '../../../../../../redux/hooks/useIs12HoursTimeFormat';
import useShipmentEditPermission from '../../../../../../redux/hooks/useShipmentEditPermission';
import useUserPreference from '../../../../../../redux/hooks/useUserPreference';
import { getStyleVariableValue } from '../../../../../../utilz';
import { formateWithMoment } from '../../../../../../utilz/dates';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import CurrencyInput from '../../../../../components/Inputs/CurrencyInput';
import FormInput from '../../../../../components/Inputs/FormInput';
import FormInputNumber from '../../../../../components/Inputs/FormInputNumber';
import FormRadioGroup from '../../../../../components/Inputs/FormRadioGroup';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import BatchLabel from '../../../../../components/Labels/BatchLabel';
import FormLabel from '../../../../../components/Labels/FormLabel';
import HighlightLabel from '../../../../../components/Labels/HighlightLabel';
import SecondaryLabel from '../../../../../components/Labels/SecondaryLabel';
import TooltipTable from '../../../../../components/Tables/TooltipTable';
import CollapsiblePanel from '../../../../../components/Tabs/CollapsiblePanel';
import TMSFiledCard from '../../../../common/components/TMSFiledCard';
import SaveCancelButtons from '../../SaveCancelButtons';
import { CargoTypeSelect, EquipmentTypeSelect } from '../../selects';

const RateShopDetailsCard = ({
  rateShopDetails,
  handleRateShopSelection,
  index,
  value,
  isRateShop = false,
  isRemoveRateEntryLoading = false,
  onClickRateEntryRemove,
  shipmentStatus,
  editMode,
  setEditMode,
  handleRateEntrySubmit,
  handleDeleteRateEntry = () => false,
}) => {
  const [activeKey, setActiveKey] = useState([]);

  const contractedTooltipContent = useMemo(() => {
    const { freightAccessorialCollection } = rateShopDetails;

    return (
      <TooltipTable>
        <tr>
          <th>{i18n.t('translation:default.fields.costName')}</th>
          <th>{i18n.t('translation:default.fields.cost')}</th>
        </tr>
        {freightAccessorialCollection?.map((accessorial) => (
          <tr key={accessorial.costType}>
            <td>{accessorial.costName}</td>
            <td>
              <CurrencyInput value={accessorial.actualAuditedCost} />{' '}
              <span className="f-10 fw-300 text-light">{accessorial.actualCurrency || accessorial.currency}</span>
            </td>
          </tr>
        ))}
      </TooltipTable>
    );
  }, [rateShopDetails]);

  const getTooltipContent = () => {
    return (
      <TooltipTable>
        <tr>
          <th>{i18n.t('translation:default.fields.costName')}</th>
          <th>{i18n.t('translation:default.fields.cost')}</th>
        </tr>
        {rateShopDetails.accessorials?.map(({ description, auditedChargeAmount, currencyCode }, key) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static
          <tr key={key}>
            <td>{description}</td>
            <td>
              <CurrencyInput value={auditedChargeAmount} />{' '}
              <span className="f-10 fw-300 text-light">{currencyCode}</span>
            </td>
          </tr>
        ))}
      </TooltipTable>
    );
  };

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const [form] = Form.useForm();

  const onFinish = (data) => {
    handleRateEntrySubmit({ ...rateShopDetails, ...data });
  };

  const effectiveTo = Form.useWatch('effectiveTo', form);
  const convertedCost = Form.useWatch('convertedCost', form);
  const hasEditPermission = useShipmentEditPermission();

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT);

  const handleCancel = () => {
    form.setFieldsValue({
      ...rateShopDetails,
      trailerkey: rateShopDetails?.trailerkey ? rateShopDetails?.trailerkey : undefined,
    });
    setEditMode(false);
  };

  useEffect(() => {
    if (editMode) {
      setActiveKey(['1']);
    }
  }, [editMode]);

  const trailerDesc = Form.useWatch('trailerDesc', form);
  const trailerKey = Form.useWatch('trailerKey', form);
  const cargoType = Form.useWatch('cargoType', form);
  const cargoTypeDesc = Form.useWatch('cargoTypeDesc', form);
  const pricingBasisDesc = Form.useWatch('pricingBasisDesc', form);
  const pricingBasis = Form.useWatch('pricingBasis', form);

  const is12HoursFormat = useIs12HoursTimeFormat();
  return (
    <Form
      onFinish={onFinish}
      form={form}
      initialValues={{
        ...rateShopDetails,
        trailerkey: rateShopDetails?.trailerkey ? rateShopDetails?.trailerkey : undefined,
      }}
    >
      <CollapsiblePanel
        forceRender
        handleTabClick={(keys) => setActiveKey(keys)}
        activeKey={activeKey}
        tabs={[
          {
            content: (
              <div>
                <Row gutter={15}>
                  <Col sm={6}>
                    <Row>
                      <Col sm={24}>
                        <FormInput
                          label={t('translation:shipmentAdd.rateShopDetails.spotQuoteNumber.label')}
                          editMode={false}
                          margin="mb-0"
                          name="spotQuoteNumber"
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col sm={6}>
                    <Row>
                      <Col sm={24}>
                        <FormInput
                          label={t('translation:shipmentAdd.rateShopDetails.equipmentTypeDetail.label')}
                          editMode={Boolean(editMode)}
                          margin="mb-3"
                          name="equipmentTypeDetail"
                        />
                      </Col>
                      <Col sm={24}>
                        <FormInput
                          label={t('translation:shipmentAdd.rateShopDetails.userSubmitted.label')}
                          editMode={false}
                          margin="mb-0"
                          name="userCreatedBy"
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col sm={6}>
                    <Row>
                      <Col sm={24}>
                        <div className="form-input-container mb-3">
                          <SecondaryLabel>{t('translation:default.fields.createdOn.title')}</SecondaryLabel>
                          <label className="label-mode">
                            <HighlightLabel>
                              {formateWithMoment(rateShopDetails.createdOn, userDateFormat)}
                            </HighlightLabel>{' '}
                            | {formateWithMoment(rateShopDetails.createdOn, is12HoursFormat ? 'hh:mm A' : 'HH:mm')}
                          </label>
                        </div>
                      </Col>
                      <div className="form-input-container mb-0">
                        <SecondaryLabel className="form-input-label">
                          {t('translation:shipmentAdd.rateShopDetails.bidExpiryDate')}
                        </SecondaryLabel>
                        <label className="label-mode">
                          <HighlightLabel>{formateWithMoment(effectiveTo, userDateFormat)}</HighlightLabel>
                        </label>
                      </div>
                    </Row>
                  </Col>
                  <Col sm={6}>
                    <Row>
                      <Col sm={24}>
                        <SecondaryLabel className="form-input-label">
                          {t('translation:shipmentAdd.rateShopDetails.bidComment.label')}
                        </SecondaryLabel>
                        <p className="m-0 fw-600 f-14 color-label">{rateShopDetails?.bidComment}</p>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <HiddenFormInput name="createdOn" />
                <HiddenFormInput name="createdBy" />
                <HiddenFormInput name="effectiveTo" />
                <HiddenFormInput name="rateShopKey" />
                <HiddenFormInput name="carrierKey" />
                <HiddenFormInput name="currencyCode" />
                <HiddenFormInput name="convertedCurrencyCode" />
                <HiddenFormInput name="convertedCost" />
              </div>
            ),
            header: (
              <Row align="middle" gutter={15}>
                <Col flex={1}>
                  <Row gutter={15} onClick={(e) => e.stopPropagation()}>
                    <Col sm={6} className="pt-3">
                      {isRateShop ? (
                        <FormRadioGroup
                          alignItems="center"
                          disabled={!hasEditPermission}
                          options={[
                            {
                              label: (
                                <span className="pointer-events-none">
                                  <label className="f-12 fw-400">
                                    {t('translation:default.fields.transportationProvider.label')}
                                  </label>
                                  <div className="mt-3">
                                    <label className="f-12 fw-600">{rateShopDetails.carrierName}</label>
                                  </div>
                                </span>
                              ),
                              value: index,
                            },
                          ]}
                          onChange={() => handleRateShopSelection(index)}
                          value={value}
                          id={`rate-radio-${index + 1}`}
                        />
                      ) : (
                        <span className="pointer-events-none">
                          <label className="f-12 fw-400">
                            {t('translation:default.fields.transportationProvider.label')}
                          </label>
                          <br />

                          <label className="f-12 fw-600">{rateShopDetails.carrierName}</label>
                        </span>
                      )}
                    </Col>
                    <Col sm={3}>
                      <Tooltip
                        color={getStyleVariableValue('--label')}
                        title={contractedTooltipContent}
                        placement="bottomLeft"
                        overlayClassName="mw-none rate-shop-cost-tool-tip"
                      >
                        <SecondaryLabel>{t('translation:shipmentAdd.rateShopDetails.contractedCost')}</SecondaryLabel>
                        <br />

                        <label className="f-12">
                          <HighlightLabel className="f-14 fw-600">
                            <CurrencyInput value={rateShopDetails.contractedShipmentCost ?? 0} />
                          </HighlightLabel>{' '}
                          {rateShopDetails.currencyCode}
                        </label>
                      </Tooltip>
                    </Col>
                    <Col sm={2}>
                      {editMode ? (
                        <FormInputNumber
                          label={t('translation:shipmentAdd.rateShopDetails.convertedCost')}
                          editMode={Boolean(editMode)}
                          margin="mb-0"
                          name="convertedCost"
                          maxLength={8}
                        />
                      ) : (
                        <Tooltip
                          color={getStyleVariableValue('--label')}
                          title={getTooltipContent()}
                          placement="bottomLeft"
                          overlayClassName="mw-none rate-shop-cost-tool-tip"
                        >
                          <SecondaryLabel>{t('translation:shipmentAdd.rateShopDetails.convertedCost')}</SecondaryLabel>
                          <br />

                          <label className="f-12">
                            <HighlightLabel className="f-14 fw-600">
                              <CurrencyInput value={convertedCost} />
                            </HighlightLabel>{' '}
                            {rateShopDetails.convertedCurrencyCode}
                          </label>
                        </Tooltip>
                      )}
                    </Col>
                    <Col sm={2}>
                      <FormInput label="Transit Days" editMode={false} margin="m-0" name="standardDaysTransit" />
                      <HiddenFormInput name="standardDaysTransit" />
                    </Col>
                    {isRateShop ? (
                      <Col sm={2}>
                        {rateShopDetails?.isRateEntry === 1 ? (
                          <FormInput
                            label={t('translation:default.fields.pricingBasis.label')}
                            editMode={false}
                            margin="mb-0"
                            name="pricingBasis"
                            maxLength="4"
                          />
                        ) : editMode ? (
                          <FormInput
                            label={t('translation:default.fields.pricingBasis.label')}
                            editMode={false}
                            margin="mb-0"
                            name="pricingBasisDesc"
                            maxLength="4"
                          />
                        ) : (
                          <TMSFiledCard
                            containerClass="mt-0 mb-5"
                            title={t('translation:default.fields.pricingBasis.label')}
                          >
                            {pricingBasisDesc ? pricingBasisDesc : pricingBasis}
                          </TMSFiledCard>
                        )}
                      </Col>
                    ) : (
                      ''
                    )}
                    <HiddenFormInput name="pricingBasis" />
                    <HiddenFormInput name="pricingBasisDesc" />
                    <Col sm={3} className="pt-5">
                      {editMode ? (
                        <CargoTypeSelect
                          label={t('translation:default.fields.cargoType.label')}
                          value={rateShopDetails.cargoType}
                          editMode={Boolean(editMode)}
                          margin="mb-0"
                          name="cargoType"
                          onChange={({ label }) => form.setFieldsValue({ cargoTypeDesc: label })}
                          disableDefaultOptions
                        />
                      ) : (
                        <TMSFiledCard
                          labelClass="mb-5"
                          containerClass="mt-0 mb-0"
                          title={t('translation:default.fields.cargoType.label')}
                        >
                          {cargoType === -1 ? 'All' : cargoTypeDesc ? cargoTypeDesc : cargoType}
                        </TMSFiledCard>
                      )}
                      <HiddenFormInput name="cargoType" />
                      <HiddenFormInput name="cargoTypeDesc" />
                    </Col>
                    <Col sm={2} className="pt-5">
                      {editMode ? (
                        <EquipmentTypeSelect
                          label={t('translation:default.fields.equipmentType.label')}
                          editMode={Boolean(editMode)}
                          margin="mb-0"
                          name="trailerKey"
                          onChange={({ label }) => form.setFieldsValue({ trailerDesc: label })}
                          rules={[
                            {
                              message: t('translation:default.fields.equipmentType.message'),
                              required: cargoType && !['AIR', 'FRF', 'OC'].includes(cargoType),
                            },
                          ]}
                        />
                      ) : (
                        <TMSFiledCard
                          labelClass="mb-5"
                          containerClass="mt-0 mb-0"
                          title={t('translation:default.fields.equipmentType.label')}
                        >
                          {trailerKey === -1 ? 'All' : trailerDesc}
                        </TMSFiledCard>
                      )}
                      <HiddenFormInput name="trailerDesc" />
                      <HiddenFormInput name="trailerKey" />
                    </Col>
                    <Col sm={4}>
                      <FormInput
                        label={t('translation:shipmentAdd.rateShopDetails.serviceDescription.label')}
                        editMode={false}
                        margin="mb-5"
                        name="serviceDesc"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col onClick={(e) => e.stopPropagation()}>
                  {rateShopDetails.status ? (
                    <Row gutter={5} align="middle">
                      <Col>
                        <FormLabel>{`${t('translation:default.fields.status.label')} :`}</FormLabel>
                      </Col>
                      <Col>
                        <BatchLabel type={rateShopDetails.status}>
                          {rateShopDetails.status.replace('NoAction', 'No Action')}
                        </BatchLabel>
                      </Col>
                    </Row>
                  ) : (
                    <Row gutter={10} align="middle">
                      {rateShopDetails?.isRateEntry === 1 ? (
                        <>
                          <Col>
                            <SaveCancelButtons
                              onCancel={handleCancel}
                              setEditMode={setEditMode}
                              editMode={Boolean(editMode)}
                              isDeleteLoading={isRemoveRateEntryLoading}
                              hideDeleteButton={
                                !SHIPMENT_RATE_ENTRY_BUTTON_REMOVE_STATUSES.includes(shipmentStatus) ||
                                shipmentStatus === 'Rated' ||
                                'Auto-Rated'
                              }
                              handleRemove={() => onClickRateEntryRemove(rateShopDetails?.rateShopKey, value)}
                              hideEditButton={!SHIPMENT_RATE_ENTRY_BUTTON_REMOVE_STATUSES.includes(shipmentStatus)}
                            />
                          </Col>
                          {SHIPMENT_RATE_ENTRY_DELETE_BUTTON_VISIBLE_STATUSES.includes(shipmentStatus) ? (
                            <Col>
                              <Popconfirm
                                placement="topRight"
                                title={t('translation:default.popConfirm.title')}
                                onConfirm={handleDeleteRateEntry}
                                okText={t('translation:default.buttons.delete')}
                                cancelText={t('translation:default.options.no')}
                                overlayClassName="delete-popup-container"
                                okType="danger"
                              >
                                <Button>Delete</Button>
                              </Popconfirm>
                            </Col>
                          ) : null}
                        </>
                      ) : null}
                    </Row>
                  )}
                </Col>
              </Row>
            ),
            tabKey: '1',
          },
        ]}
      />
    </Form>
  );
};

export default RateShopDetailsCard;
